<template>
    <NuxtLayout name="main" :dark="true">
        <div :class="css.container" class="container pt-[64px]">
            <!-- * 顶部内容 -->
            <section>
                <div class="text-emphas text-[44px] leading-[76px] font-bold">{{ $t("GeneMutationFlow") }}</div>
                <div class="flex flex-wrap mx-[-6px] mb-[-6px] mt-[2px]">
                    <div class="m-[6px] flex items-center rounded-full min-h-[40px] px-[16px] bg-color4 leading-[22px]">
                        {{ $t("GeneInformation") }}
                    </div>
                    <div class="m-[6px] flex items-center rounded-full min-h-[40px] px-[16px] bg-color4 leading-[22px]">
                        {{ $t("GeneticSequence") }}
                    </div>
                    <div class="m-[6px] flex items-center rounded-full min-h-[40px] px-[16px] bg-color4 leading-[22px]">
                        {{ $t("MutationViewer") }}
                    </div>
                </div>

                <div class="flex justify-center bg-[#D9DFF2] pt-[80px] pb-[88px] mt-[40px]">
                    <div class="relative pb-[34px]">
                        <img class="w-[96px] h-[96px]" src="https://rdgta.cdn.cyagen.com/rddc/pictures/bb8bb8dd-9a5a-4a6f-8228-a1fee9b6e69a.webp" />
                        <div
                            class="whitespace-nowrap absolute bottom-0 left-1/2 text-emphas text-[18px] leading-[34px] font-bold"
                            style="transform: translateX(-50%)">
                            {{ $t("GeneticInformation") }}
                        </div>
                    </div>
                    <img class="w-[136px] h-[16px] mx-[32px] mt-[40px]" src="https://rdgta.cdn.cyagen.com/rddc/pictures/6d1495e6-5fcd-4533-93b3-775f5c5c10fc.webp" />
                    <div class="relative pb-[34px]">
                        <img class="w-[96px] h-[96px]" src="https://rdgta.cdn.cyagen.com/rddc/pictures/44208c5d-5d5f-4dd5-a14f-d0b12534df29.webp" />
                        <div
                            class="whitespace-nowrap absolute bottom-0 left-1/2 text-emphas text-[18px] leading-[34px] font-bold"
                            style="transform: translateX(-50%)">
                            {{ $t("TranscriptSequence") }}
                        </div>
                    </div>
                    <img class="w-[136px] h-[16px] mx-[32px] mt-[40px]" src="https://rdgta.cdn.cyagen.com/rddc/pictures/f3ca63c8-d58e-4abe-b7b2-6f89d1df91e7.webp" />
                    <div class="relative pb-[34px]">
                        <img class="w-[96px] h-[96px]" src="https://rdgta.cdn.cyagen.com/rddc/pictures/90ef2c25-312c-49a1-96af-35cec96b528b.webp" />
                        <div
                            class="whitespace-nowrap absolute bottom-0 left-1/2 text-emphas text-[18px] leading-[34px] font-bold"
                            style="transform: translateX(-50%)">
                            {{ $t("GeneticMutation") }}
                        </div>
                    </div>
                </div>
            </section>

            <!-- * 模块一 -->
            <section class="mt-[40px]">
                <div class="text-emphas text-[24px] leading-[40px] font-bold">01. {{ $t("GeneSearch") }}</div>
                <div class="mt-[4px]">{{ $t("MutationseekStep1Descript") }}</div>
                <div class="mt-[16px] py-[24px] px-[48px] bg-color7" v-loading="data.search.load">
                    <div class="table">
                        <div class="table-row">
                            <div class="table-cell">{{ useColon($t("Gene")) }}</div>
                            <div class="table-cell">
                                <default-search
                                    class="ml-[16px]"
                                    v-model="data.search.name"
                                    mode="select"
                                    type="gene"
                                    ref="search_input"
                                    @search="(response) => methods.change(data.search, response)"
                                    :width="440"
                                    :query="{ tax_names: ['Human'] }"
                                    :placeholder="$t('PleaseEnterGeneNameOrNCBlID')" />
                            </div>
                            <div class="table-cell">
                                <img
                                    class="ml-[16px]"
                                    v-if="data.search.id && data.search.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/cd5d21ab-8f0a-49e8-9273-5224eecfefae.webp" />
                                <img
                                    class="ml-[16px]"
                                    v-if="data.search.id && !data.search.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/7800f4de-0e26-4995-bf50-1834090887b6.webp" />
                            </div>
                        </div>

                        <div class="table-row">
                            <div class="table-cell pt-[16px]"></div>
                            <div class="table-cell pt-[16px]">
                                <ui-button
                                    class="ml-[16px]"
                                    :width="104"
                                    :disabled="!data.search.id || !data.search.transcript"
                                    @click="methods.confirm(data.search)">
                                    {{ $t("Search") }}
                                </ui-button>
                                <ui-button
                                    class="ml-[16px]"
                                    :width="104"
                                    :class="css_button.cancel"
                                    @click="methods.reset(data.search, search_input)">
                                    {{ $t("Reset") }}
                                </ui-button>
                            </div>
                            <div class="table-cell pt-[16px]"></div>
                        </div>
                    </div>

                    <!-- * 结果 -->
                    <div class="mt-[24px]" v-if="data.search.response">
                        <div class="mb-[24px] w-full h-[1px] bg-uicolor4/50"></div>
                        <div>
                            <span>{{ useColon($t("Result")) }}&nbsp;</span>
                            <ProjectRoute :class="css_normalize.assets" :to="{ name: 'Gene', params: { id: data.search.response.id } }">
                                {{ $t("ClickHereToViewTheGene") }} - {{ data.search.response.name }}
                            </ProjectRoute>
                        </div>
                    </div>
                </div>
            </section>

            <!-- * 模块二 -->
            <section class="mt-[40px]">
                <div class="text-emphas text-[24px] leading-[40px] font-bold">02. {{ $t("SelectSequence") }}</div>
                <div class="mt-[4px]">{{ $t("MutationseekStep2Descript") }}</div>
                <div class="mt-[16px] py-[24px] px-[48px] bg-color7" v-loading="data.sequence.load">
                    <!-- * 获取第一步数据 -->
                    <div
                        class="mt-[8px] cursor-pointer text-main underline underline-offset-2"
                        v-if="data.search.status == 'success'"
                        @click="methods.obtain(data.sequence, data.search, sequence_input)">
                        {{ $t("GetGeneFromStep1") }} &gt;&gt;
                    </div>

                    <!-- * 输入模型 -->
                    <div class="table mt-[24px]">
                        <div class="table-row">
                            <div class="table-cell">{{ useColon($t("Gene")) }}</div>
                            <div class="table-cell">
                                <default-search
                                    class="ml-[16px]"
                                    v-model="data.sequence.name"
                                    mode="select"
                                    type="gene"
                                    ref="sequence_input"
                                    @search="(response) => methods.change(data.sequence, response)"
                                    :width="440"
                                    :query="{ tax_names: ['Human'] }"
                                    :placeholder="$t('PleaseEnterGeneNameOrNCBlID')" />
                            </div>
                            <div class="table-cell">
                                <img
                                    class="ml-[16px]"
                                    v-if="data.sequence.id && data.sequence.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/09aa7688-cb7c-421c-911d-33a7f6e4924d.webp" />
                                <img
                                    class="ml-[16px]"
                                    v-if="data.sequence.id && !data.sequence.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/a2f09ecb-1848-41fd-a436-c09e74ab7d2b.webp" />
                            </div>
                        </div>

                        <div class="table-row">
                            <div class="table-cell pt-[16px]">{{ useColon($t("Transcript")) }}</div>
                            <div class="table-cell pt-[16px]">
                                <ui-select
                                    class="ml-[16px]"
                                    v-model="data.sequence.transcript"
                                    :width="440"
                                    :disabled="!data.sequence.transcript.length"
                                    :candidates="data.sequence.transcripts"
                                    :placeholder="$t('PleaseSelectTranscript')"
                                    @change="methods.transcript(data.sequence)" />
                            </div>
                            <div class="table-cell pt-[16px]">
                                <img
                                    class="ml-[16px]"
                                    v-if="data.sequence.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/23e2aea3-6b6c-4e65-ac5c-3970cd3e0f43.webp" />
                            </div>
                        </div>

                        <div class="table-row">
                            <div class="table-cell pt-[16px]"></div>
                            <div class="table-cell pt-[16px]">
                                <ui-button
                                    class="ml-[16px]"
                                    :width="104"
                                    :disabled="!data.sequence.id || !data.sequence.transcript"
                                    @click="methods.confirm(data.sequence)">
                                    {{ $t("Search") }}
                                </ui-button>
                                <ui-button
                                    class="ml-[16px]"
                                    :width="104"
                                    :class="css_button.cancel"
                                    @click="methods.reset(data.sequence, sequence_input)">
                                    {{ $t("Reset") }}
                                </ui-button>
                            </div>
                            <div class="table-cell pt-[16px]"></div>
                        </div>
                    </div>

                    <!-- * 结果 -->
                    <div class="mt-[24px]" v-if="data.sequence.response">
                        <div class="mb-[24px] w-full h-[1px] bg-uicolor4/50"></div>
                        <div>
                            <span>{{ useColon($t("Result")) }}&nbsp;</span>
                            <ProjectRoute
                                :class="css_normalize.assets"
                                :to="{
                                    name: 'BioSequenceResult',
                                    params: { id: data.sequence.response.id },
                                    query: { id: data.sequence.response.transcript },
                                }">
                                {{ $t("ClickHereToViewTheTranscript") }} - {{ data.sequence.response.transcript }}
                            </ProjectRoute>
                        </div>
                    </div>
                </div>
            </section>

            <!-- * 模块三 -->
            <section class="mt-[40px]">
                <div class="text-emphas text-[24px] leading-[40px] font-bold">03. {{ $t("SearchForMutation") }}</div>
                <div class="mt-[4px]">{{ $t("WorkflowStep1Descript") }}</div>
                <div class="mt-[16px] py-[24px] px-[48px] bg-color7" v-loading="data.mutation.load">
                    <!-- * 获取第一步数据 -->
                    <div
                        class="mt-[8px] cursor-pointer text-main underline underline-offset-2"
                        v-if="data.sequence.status == 'success'"
                        @click="methods.obtain(data.mutation, data.sequence, mutation_input)">
                        {{ $t("GetGeneAndTranscriptFromStep2") }} &gt;&gt;
                    </div>

                    <!-- * 输入模型 -->
                    <div class="table mt-[24px]">
                        <div class="table-row">
                            <div class="table-cell">{{ useColon($t("Gene")) }}</div>
                            <div class="table-cell">
                                <default-search
                                    class="ml-[16px]"
                                    v-model="data.mutation.name"
                                    mode="select"
                                    type="gene"
                                    ref="mutation_input"
                                    @search="(response) => methods.change(data.mutation, response)"
                                    :width="440"
                                    :query="{ tax_names: ['Human'] }"
                                    :placeholder="$t('PleaseEnterGeneNameOrNCBlID')" />
                            </div>
                            <div class="table-cell">
                                <img
                                    class="ml-[16px]"
                                    v-if="data.mutation.id && data.mutation.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/418a91a5-5e61-4d2f-a31a-7e0cc09cc6bd.webp" />
                                <img
                                    class="ml-[16px]"
                                    v-if="data.mutation.id && !data.mutation.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/e9bb3951-8392-4537-abd1-f1c391f51ba3.webp" />
                            </div>
                        </div>

                        <div class="table-row">
                            <div class="table-cell pt-[16px]">{{ useColon($t("Transcript")) }}</div>
                            <div class="table-cell pt-[16px]">
                                <ui-select
                                    class="ml-[16px]"
                                    v-model="data.mutation.transcript"
                                    :width="440"
                                    :disabled="!data.mutation.transcript.length"
                                    :candidates="data.mutation.transcripts"
                                    :placeholder="$t('PleaseSelectTranscript')"
                                    @change="methods.transcript(data.mutation)" />
                            </div>
                            <div class="table-cell pt-[16px]">
                                <img
                                    class="ml-[16px]"
                                    v-if="data.mutation.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/5acf99be-04f4-4eea-9b1e-147a176e0932.webp" />
                            </div>
                        </div>

                        <div class="table-row">
                            <div class="table-cell pt-[16px]">
                                <span>{{ useColon($t("Mutation")) }}</span>
                                <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                    <render-mutation-describe />
                                </helper-modal>
                            </div>
                            <div class="table-cell pt-[16px]">
                                <ui-input
                                    class="ml-[16px]"
                                    v-model="data.mutation.keyword"
                                    :width="440"
                                    :disabled="!data.mutation.transcripts.length"
                                    :maxlength="50"
                                    :placeholder="$t('PleaseEnterMutation')" />
                            </div>
                            <div class="table-cell pt-[16px]">
                                <img
                                    class="ml-[16px]"
                                    v-if="data.mutation.keyword && data.mutation.status == 'success'"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/ba172ece-84fc-4065-8fd6-c1691d80011e.webp" />
                                <img
                                    class="ml-[16px]"
                                    v-if="data.mutation.keyword && data.mutation.status == 'error'"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/a81457ee-e27e-42c1-9932-f5a84b405f41.webp" />
                            </div>
                        </div>

                        <div class="table-row">
                            <div class="table-cell pt-[16px]"></div>
                            <div class="table-cell pt-[16px]">
                                <ui-button
                                    class="ml-[16px]"
                                    :width="104"
                                    :disabled="!data.mutation.id || !data.mutation.keyword || !data.mutation.transcript"
                                    @click="methods.search(data.mutation)">
                                    {{ $t("Search") }}
                                </ui-button>
                                <ui-button
                                    class="ml-[16px]"
                                    :width="104"
                                    :class="css_button.cancel"
                                    @click="methods.reset(data.mutation, mutation_input)">
                                    {{ $t("Reset") }}
                                </ui-button>
                            </div>
                            <div class="table-cell pt-[16px]"></div>
                        </div>
                    </div>

                    <!-- * 结果 -->
                    <div class="mt-[24px]" v-if="data.mutation.response">
                        <div class="mb-[24px] w-full h-[1px] bg-uicolor4/50"></div>
                        <div>
                            <span>{{ useColon($t("Result")) }}&nbsp;</span>
                            <ProjectRoute
                                :class="css_normalize.assets"
                                :to="{
                                    name: 'BioVariantResult',
                                    params: { id: data.mutation.response.id },
                                    query: { id: data.mutation.response.transcript, keyword: data.mutation.response.keyword },
                                }">
                                {{ $t("ClickHereToViewMutation") }} - {{ data.mutation.response.name }}: {{ data.mutation.response.keyword || "" }}
                            </ProjectRoute>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </NuxtLayout>
</template>

<script lang="tsx" setup>
//* 组件库
import { UiInput, UiSelect, UiButton } from "various-ui";
//* 公共组件
import { DefaultSearch } from "@/components/controls";
import { HelperModal } from "@/components/windows";
//* 组件属性
import { useComposable } from "./src/composable";
//* 样式
import css_normalize from "@/assets/style/src/index.module.less";
import css_button from "@/assets/style/src/button.module.less";
import css from "./index.module.less";

//* 插件
const i18n = useI18n();

//* 组合函数
const { data, nodes, methods } = useComposable();
const { search_input, sequence_input, mutation_input } = nodes;

//* 渲染函数（突变模板格式说明）
const renderMutationDescribe = defineComponent({
    render() {
        return (
            <div>
                <div>{i18n.t("FastExpressDescribe1")}</div>
                <div>（1）c.123G&gt;A</div>
                <div>（2）c.123_124TG&gt;C</div>
                <div>（3）c.169+2T&gt;C</div>
                <div>（4）c.45_46insGC</div>
                <div>（5）c.36_38del</div>
                <div>（6）c.45_47delinsTT</div>
                <div>（7）c.-1G&gt;T</div>
                <div>（8）c.*16G&gt;T</div>
                <div>（9）c.23+4_23+5GG&gt;CT</div>
                <div>（10）p.Ser3435Ter</div>
                <div style="margin-top: 10px">{i18n.t("FastExpressDescribe2")}</div>
            </div>
        );
    },
});
</script>
